// ---------------------------------------------------------------------------
// A round plan, summarised.
//
// Fifteen individual chips do not fit a phone row and do not read as anything
// once they do, so every list summarises the plan as grouped counts plus a
// total: `L 10  N 4  C 1  ·  15 rounds`. Only the lobby's builder shows one chip
// per round, because there the position is the point.
//
// Two things it must not do. It must not resize as the host edits the plan —
// the counts are tabular and two digits wide from the start, so 9 becoming 10
// does not shove the total sideways — and it must not read out as "L 10 N 4 C 1"
// to anyone listening. The visual row is hidden from the accessibility tree and
// a spoken sentence sits beside it.
//
// **It is one line that never wraps.** It used to wrap, and the estimate is a
// sentence whose length depends on the clocks — "about a minute" against "about
// an hour and 5 minutes" — so dragging a timer in the lobby could push it onto
// a second line and move the whole builder under the host's hand. A fixed row
// height and a scroll of its own is the trade: too narrow scrolls sideways
// rather than growing downward. The group chips get a floor wide enough for all
// three, so gaining a round type slides nothing along either.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { GameSettings, RoundType } from '@countdown/shared';
import { estimateDurationMs, formatDuration, groupedSequence, roundLabel } from '../../lib/format.js';
import { ROUND_CHIP_FILL, ROUND_CHIP_LETTER } from './RoundTypeChip.js';

/** Grouped counts plus a total: `L 10  N 4  C 1  ·  15 rounds`. */
export function RoundSequenceSummary(props: {
  sequence: RoundType[];
  showEstimate?: boolean;
  settings?: GameSettings;
}): ReactElement {
  const { sequence, showEstimate = false, settings } = props;
  const groups = groupedSequence(sequence);
  const total = sequence.length === 1 ? '1 round' : `${sequence.length} rounds`;
  const estimate = showEstimate && settings ? formatDuration(estimateDurationMs(settings)) : null;

  // No "rounds" in the group clause: it would put "1 rounds" in front of a
  // screen reader on a plan of one numbers round.
  const spoken = [
    groups.map((group) => `${group.count} ${roundLabel(group.type).toLowerCase()}`).join(', '),
    total,
    estimate,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join('. ');

  return (
    <div className="flex h-[34px] items-center gap-2 overflow-x-auto whitespace-nowrap">
      <span className="sr-only">{spoken}</span>

      {/* Room for all three chips whether or not the plan has all three, so a
          host adding a numbers round to a letters-only plan does not push the
          total and the estimate along. */}
      <span aria-hidden="true" className="flex min-w-[150px] shrink-0 items-center gap-2">
        {groups.map((group) => (
          <span
            key={group.type}
            data-round-type={group.type}
            className={`flex items-center gap-1.5 rounded-chip px-2 py-1.5 font-mono transition-colors duration-150 ease-out ${ROUND_CHIP_FILL[group.type]}`}
          >
            <span className="text-[13px] leading-none font-bold">
              {ROUND_CHIP_LETTER[group.type]}
            </span>
            <span className="min-w-[2ch] text-center text-[12px] leading-none font-semibold tabular">
              {group.count}
            </span>
          </span>
        ))}
      </span>

      <span aria-hidden="true" className="shrink-0 font-mono text-[12px] text-ink-dim tabular">
        {total}
      </span>

      {estimate !== null && (
        <span aria-hidden="true" className="shrink-0 font-mono text-[12px] text-ink-dim">
          {estimate}
        </span>
      )}
    </div>
  );
}
